// <facet-filters>: AJAX collection/search filtering, sort, and pagination via
// Section Rendering. Shows a dimension-matched skeleton grid while a request is
// in flight (zero CLS), and sets aria-busy on the results region.
customElements.define(
  "facet-filters",
  class extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener("change", (e) => {
        // Price inputs emit `change` on blur and on Enter. Applying then would fire
        // a request mid-edit and re-render the form out from under the caret, so
        // anything inside [data-facet-manual] waits for an explicit submit.
        if (e.target.closest("[data-facet-manual]")) return;
        if (e.target.closest("[data-facets],[data-sort]")) this.apply(e.target);
      });
      this.addEventListener("submit", (e) => {
        if (!e.target.closest("[data-facets]")) return; // ignore nested forms (e.g. add-to-cart on product cards)
        e.preventDefault();
        this.apply(e.target);
      });
      this.addEventListener("click", (e) => {
        const clear = e.target.closest("[data-clear]");
        if (clear) { e.preventDefault(); this.go(clear.getAttribute("href")); return; }
        const page = e.target.closest("[data-pagination] a");
        // Pagination is an AJAX swap, not a navigation, so nothing resets the scroll
        // position: page 2 renders while you are still looking at page 1's footer.
        // Filter/sort swaps deliberately do not scroll (see go()).
        if (page) { e.preventDefault(); this.go(page.getAttribute("href"), { scrollToResults: true }); }
      });
      // Native <details> doesn't close on outside click / Escape; do it for the
      // desktop sort dropdown (scoped to .jg-sort-dd so the accordions stay open).
      document.addEventListener("click", (e) => {
        this.querySelectorAll("details.jg-sort-dd[open]").forEach((d) => { if (!d.contains(e.target)) d.removeAttribute("open"); });
      });
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape") this.querySelectorAll("details.jg-sort-dd[open]").forEach((d) => d.removeAttribute("open"));
      });
    }
    apply(source) {
      const form = (source && source.closest("[data-facets]")) || this.querySelector("[data-facets]");
      const params = new URLSearchParams(form ? new FormData(form) : "");
      const sortWrap = (source && source.closest("[data-sort]")) || this.querySelector("[data-sort]");
      const checked = sortWrap && sortWrap.querySelector('input[name="sort_by"]:checked');
      if (checked) params.set("sort_by", checked.value);
      this.go(window.location.pathname + "?" + params.toString());
    }
    closeOverlays() {
      this.querySelectorAll("theme-drawer.is-open").forEach((d) => {
        if (typeof d.close === "function") d.close();
      });
    }
    showSkeleton() {
      const results = this.querySelector("[data-results]");
      if (!results) return;
      const grid = results.querySelector(".jg-grid");
      const n = grid ? Math.min(grid.children.length || 8, 12) : 8;
      results.setAttribute("aria-busy", "true");
      if (grid) grid.innerHTML = window.theme.skeleton("product-card", n);
    }
    go(url, opts) {
      const u = new URL(url, window.location.origin);
      // A vendor or type page (/collections/vendors?q=Sony) and a search results page
      // carry their subject in the query string. The forms submit it as a hidden field,
      // but a link built elsewhere may not, and without it the request is the bare
      // /collections/vendors -- no vendor at all, which broke filtering on brand pages.
      // So on a same-page request, keep q (and search's type) unless the URL sets its own.
      if (u.pathname === window.location.pathname) {
        const here = new URLSearchParams(window.location.search);
        ["q", "type"].forEach((k) => {
          if (here.has(k) && !u.searchParams.has(k)) u.searchParams.set(k, here.get(k));
        });
      }
      // The filter drawer lives inside [data-facets-wrap], which the swap below replaces
      // wholesale, so the open drawer is about to be destroyed. Only close() hands back
      // the page scroll lock and the focus trap it took -- without this the shopper was
      // left on a page that could not scroll, holding a focus trap on a detached node.
      // Closing before the fetch also means Apply reveals the skeleton it triggered.
      this.closeOverlays();
      this.showSkeleton();
      // section_id is set as a real parameter rather than appended with "&". A clear link
      // is the bare collection URL with no query of its own, so appending produced
      // /collections/x&section_id=main-collection -- which Shopify reads as a collection
      // handle, not a section request. It 404s, a 404 resolves so the catch below never
      // sees it, and the 404 page holds no [data-facets-wrap] to swap in: Clear all left
      // the skeleton grid up and never came back. Applying with nothing ticked hit the
      // same path, because pathname + "?" + "" has no search either.
      const req = new URL(u);
      req.searchParams.set("section_id", this.sectionId);
      fetch(req.pathname + req.search)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, "text/html");
          ["[data-facets-wrap]", "[data-results]"].forEach((sel) => {
            const fresh = doc.querySelector(sel);
            const cur = this.querySelector(sel);
            if (fresh && cur) cur.replaceWith(fresh);
          });
          // The swapped-in cards are DOM that no paint has ever touched, so their
          // wishlist hearts would come back blank -- saved products stopped looking
          // saved the moment you turned the page. theme.renderProductCards repaints for
          // the rails it builds; a section swap has to do the same.
          if (window.theme && window.theme.paintWishlist) window.theme.paintWishlist();
          window.history.replaceState({}, "", u.pathname + u.search);
          if (opts && opts.scrollToResults) this.scrollToResults();
        })
        .catch(() => { window.location.href = u.href; });
    }
    // Height comes from the header element, not --jg-header-h: that token is 4.5rem,
    // so reading it back needs unit math that breaks silently if it is ever
    // re-expressed in px. The extra 16px matches the +1.5rem offset the sticky
    // sidebar and PDP gallery already use.
    scrollToResults() {
      const results = this.querySelector("[data-results]");
      if (!results) return;
      const header = document.querySelector("header.jg-header");
      const headerH = (header && header.offsetHeight) || 72;
      const top = results.getBoundingClientRect().top + window.scrollY - headerH - 16;
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({ top: Math.max(0, top), behavior: reduce ? "auto" : "smooth" });
    }
  }
);
